import { observer } from 'mobx-react-lite'
import { Button } from '@/components/Button'
import { TextFieldContainer } from '@/components/inputs/TextFieldContainer'
import { SelectContainer } from '@/components/inputs/SelectContainer'
import { RadioBooleanContainer } from '@/components/inputs/RadioContainer'
import { BUSINESS_ACTIVITY_CODES } from '@/features/jobs/details/tax-type/tax-prep-organizer/data/business-activity-codes'
import type { BusinessAccordionViewModel } from './BusinessAccordionViewModel'
import { FormFieldInfoPanelLabel } from '@/features/jobs/details/tax-type/tax-prep-organizer/components/FormFieldInfoPanelLabel'
import {
  EinInfoContent,
  BusinessActivityCodeInfoContent,
  BusinessActivityDescriptionInfoContent,
  ActiveParticipationInfoContent,
  BusinessIncomeTaxTreatmentInfoContent,
  ProfessionalGamblingInfoContent,
  NotaryIncomeInfoContent,
  PaperRouteMinorInfoContent,
  PaymentsForWorkBusinessInfoContent,
  BusinessSetupInfoContent,
} from '../content'
import { FormContainer } from '@/components/FormContainer'
import { useId } from 'react'
import {
  FormFieldGroup,
  FormFieldSet,
  FormFieldSetLegend,
} from '@/components/Form'
import { BUSINESS_TYPES } from '../../../data/business-types'
import { INVENTORY_METHODS } from '../../../data/inventory-methods'
import { AddressFieldSet } from '@/features/jobs/details/tax-type/tax-prep-organizer/components/AddressFieldSet'
import { FormFieldConditional } from '../../../components/FormFieldConditional'
import { masks } from '@/utils/input-mask-util'
import { FormFieldRequiredLabel } from '../../../components/FormFieldRequiredLabel'
import { StepCardListItemContainer } from '../../../components/StepCardListItemContainer'

interface BusinessAccordionProps {
  vm: BusinessAccordionViewModel
  onRemove: () => void
  testId?: string
}

export const BusinessAccordion = observer(function BusinessAccordion({
  vm,
  onRemove,
  testId,
}: BusinessAccordionProps) {
  const formId = useId()

  return (
    <StepCardListItemContainer
      icon="business"
      title={vm.displayName}
      subtitle={vm.displaySubtitle}
      extras={
        <Button
          iconLeft="delete"
          variant="text-destructive"
          label="Remove"
          onPress={onRemove}
          testId={testId ? `${testId}-remove-button` : undefined}
        />
      }
      isExpanded={vm.isExpanded}
      onExpandedChange={vm.toggleExpanded}
      testId={testId}
    >
      <FormContainer id={formId}>
        <FormFieldGroup stackWhenCompact>
          <SelectContainer
            model={vm}
            field="ownerType"
            label={<FormFieldRequiredLabel label="Owner" />}
            items={vm.owners}
            getKey={({ type }) => type}
            getLabel={({ label }) => label}
            required
            testId={testId ? `${testId}-owner-select` : undefined}
          />
          <SelectContainer
            model={vm}
            field="businessType"
            label={
              <FormFieldInfoPanelLabel
                label="How is your business set up?"
                infoContent={BusinessSetupInfoContent}
                required
                infoTestId={
                  testId ? `${testId}-business-type-info-button` : undefined
                }
              />
            }
            items={BUSINESS_TYPES}
            getKey={([code]) => code}
            getLabel={([, label]) => label}
            required
            testId={testId ? `${testId}-business-type-select` : undefined}
          />
          <SelectContainer
            model={vm}
            field="businessCode"
            label={
              <FormFieldInfoPanelLabel
                label="Business activity code"
                infoContent={BusinessActivityCodeInfoContent}
                infoTestId={
                  testId ? `${testId}-activity-code-info-button` : undefined
                }
              />
            }
            items={BUSINESS_ACTIVITY_CODES}
            getKey={([code]) => code}
            getLabel={([, label]) => label}
            testId={testId ? `${testId}-activity-code-select` : undefined}
          />
        </FormFieldGroup>
        <FormFieldGroup stackWhenCompact>
          <TextFieldContainer
            model={vm}
            field="businessName"
            label={
              <FormFieldRequiredLabel
                label="Business name"
                required={vm.isLLC}
              />
            }
            placeholder="e.g. Acme Freelance LLC"
            required={vm.isLLC}
            testId={testId ? `${testId}-business-name-input` : undefined}
          />
          <TextFieldContainer
            model={vm}
            field="ein"
            label={
              <FormFieldInfoPanelLabel
                label="EIN"
                infoContent={EinInfoContent}
                required={vm.isLLC}
                infoTestId={testId ? `${testId}-ein-info-button` : undefined}
              />
            }
            placeholder="e.g. 12-3456789"
            mask={masks.ein}
            required={vm.isLLC}
            data-private
            testId={testId ? `${testId}-ein-input` : undefined}
          />
          <TextFieldContainer
            model={vm}
            field="principleBusiness"
            label={
              <FormFieldInfoPanelLabel
                label="Briefly describe your business activity"
                required={vm.isSoleProp}
                infoContent={BusinessActivityDescriptionInfoContent}
                infoTestId={
                  testId
                    ? `${testId}-activity-description-info-button`
                    : undefined
                }
              />
            }
            placeholder="e.g. Software development and consulting"
            required={vm.isSoleProp}
            testId={testId ? `${testId}-activity-description-input` : undefined}
          />
        </FormFieldGroup>
      </FormContainer>

      <AddressFieldSet
        model={vm.address}
        legend="Business Address"
        testId={testId}
      />

      <FormFieldSet>
        <FormFieldSetLegend>Additional Questions</FormFieldSetLegend>
        <RadioBooleanContainer
          model={vm}
          field="didMateriallyParticipateDuringYear"
          label={
            <FormFieldInfoPanelLabel
              label={`Did you actively participate in this business during ${vm.year}?`}
              infoContent={ActiveParticipationInfoContent}
              infoTestId={
                testId
                  ? `${testId}-active-participation-info-button`
                  : undefined
              }
            />
          }
          orientation="horizontal"
          testId={testId ? `${testId}-active-participation` : undefined}
        />
        <RadioBooleanContainer
          model={vm}
          field="businessWasStartedOrAcquiredDuringYear"
          label={`Did you start or acquire this business in ${vm.year}?`}
          orientation="horizontal"
          testId={testId ? `${testId}-business-started-or-acquired` : undefined}
        />
        <RadioBooleanContainer
          model={vm}
          field="carryToForm8960"
          label={
            <FormFieldInfoPanelLabel
              label="Is this income treated as passive for tax purposes?"
              infoContent={BusinessIncomeTaxTreatmentInfoContent}
              infoTestId={
                testId ? `${testId}-income-passive-info-button` : undefined
              }
            />
          }
          orientation="horizontal"
          testId={testId ? `${testId}-income-passive` : undefined}
        />
        <RadioBooleanContainer
          model={vm}
          field="professionalGambler"
          label={
            <FormFieldInfoPanelLabel
              label="Is this business related to professional gambling?"
              infoContent={ProfessionalGamblingInfoContent}
              infoTestId={
                testId
                  ? `${testId}-professional-gambling-info-button`
                  : undefined
              }
            />
          }
          orientation="horizontal"
          testId={testId ? `${testId}-professional-gambling` : undefined}
        />
        <RadioBooleanContainer
          model={vm}
          field="exemptNotaryIncome"
          label={
            <FormFieldInfoPanelLabel
              label="Did this business include income from notary work?"
              infoContent={NotaryIncomeInfoContent}
              infoTestId={
                testId ? `${testId}-notary-income-info-button` : undefined
              }
            />
          }
          orientation="horizontal"
          testId={testId ? `${testId}-notary-income` : undefined}
        />
        <RadioBooleanContainer
          model={vm}
          field="paperRouteExcludedFromSe"
          label={
            <FormFieldInfoPanelLabel
              label="Did anyone under age 18 earn paper route income for this business?"
              infoContent={PaperRouteMinorInfoContent}
              infoTestId={
                testId ? `${testId}-paper-route-minor-info-button` : undefined
              }
            />
          }
          orientation="horizontal"
          testId={testId ? `${testId}-paper-route-minor` : undefined}
        />
        <FormFieldSet>
          <RadioBooleanContainer
            model={vm}
            field="didMakePaymentsRequiring1099"
            label={
              <FormFieldInfoPanelLabel
                label="Did you make payments for work that require issuing 1099 for this business?"
                infoContent={PaymentsForWorkBusinessInfoContent}
                infoTestId={
                  testId
                    ? `${testId}-payments-requiring-1099-info-button`
                    : undefined
                }
              />
            }
            hint="Applies to payments for services or contractor work, which may require a 1099."
            orientation="horizontal"
            testId={testId ? `${testId}-payments-requiring-1099` : undefined}
          />
          <FormFieldConditional when={vm.didMakePaymentsRequiring1099}>
            <RadioBooleanContainer
              model={vm}
              field="willFileForm1099"
              label="Will you file the required 1099 forms for those payments?"
              orientation="horizontal"
              testId={testId ? `${testId}-file-1099-forms` : undefined}
            />
          </FormFieldConditional>
        </FormFieldSet>
        <FormFieldSet>
          <RadioBooleanContainer
            model={vm}
            field="hasInventoryFlag"
            label={`Did you have an inventory in ${vm.year}?`}
            hint="Inventory includes physical items you bought or produced to sell."
            orientation="horizontal"
            testId={testId ? `${testId}-inventory` : undefined}
          />
          <FormFieldConditional when={vm.hasInventoryFlag}>
            <SelectContainer
              model={vm}
              field="inventoryMethod"
              label="How did you value your year-end inventory?"
              items={INVENTORY_METHODS}
              getKey={([code]) => code}
              getLabel={([, label]) => label}
              required
              testId={testId ? `${testId}-inventory-method-select` : undefined}
            />
          </FormFieldConditional>
        </FormFieldSet>
      </FormFieldSet>
    </StepCardListItemContainer>
  )
})
